import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../services/adapters';
import './WebhookLogs.css';

function WebhookLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchLogs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/webhook/logs`, {
        params: { limit: 100 }
      });
      setLogs(data);
    } catch (error) {
      console.error('Failed to load webhook logs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    if (!autoRefresh) return undefined;

    const intervalId = setInterval(fetchLogs, 5000);
    return () => clearInterval(intervalId);
  }, [autoRefresh, fetchLogs]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const handleDelete = async (logId) => {
    if (!window.confirm('確定要刪除這條記錄嗎？')) return;

    try {
      await axios.delete(`${API_URL}/webhook/logs/${logId}`);
      setLogs((prev) => prev.filter((l) => l.id !== logId));
      if (selectedLog?.id === logId) {
        setSelectedLog(null);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('刪除失敗：' + error.message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('已複製到剪貼簿');
  };

  if (loading) {
    return <div className="webhook-logs-container"><div className="loading">載入中...</div></div>;
  }

  return (
    <div className="webhook-logs-container">
      <div className="webhook-header">
        <h1>🔗 Webhook 接收記錄</h1>
        <div className="webhook-info">
          <div className="api-url">
            <strong>API 端點:</strong>
            <code>https://multi-counter.bancheng.tech/api/webhook/sheets-update</code>
            <button onClick={() => copyToClipboard('https://multi-counter.bancheng.tech/api/webhook/sheets-update')}>
              📋 複製
            </button>
          </div>
          <div className="auto-refresh">
            <label>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              自動更新
            </label>
          </div>
        </div>
      </div>

      <div className="webhook-content">
        <div className="logs-list">
          <h2>請求列表 ({logs.length})</h2>
          {logs.length === 0 ? (
            <div className="empty-state">尚無請求記錄</div>
          ) : (
            logs.map((log) => (
              <div
                key={log.id}
                className={`log-item ${selectedLog?.id === log.id ? 'selected' : ''}`}
                onClick={() => setSelectedLog(log)}
              >
                <div className="log-header">
                  <span className={`method-badge ${log.method?.toLowerCase()}`}>
                    {log.method || 'UNKNOWN'}
                  </span>
                  <span className="log-time">{formatTimestamp(log.createdAt)}</span>
                </div>
                <div className="log-url">{log.url || log.path || '/'}</div>
                <div className="log-meta">
                  <span>IP: {log.ip || 'unknown'}</span>
                  {log.query && Object.keys(log.query).length > 0 && (
                    <span className="badge">Query: {Object.keys(log.query).length}</span>
                  )}
                  {log.body && Object.keys(log.body).length > 0 && (
                    <span className="badge">Body: {Object.keys(log.body).length}</span>
                  )}
                </div>
                <button
                  className="delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(log.id);
                  }}
                >
                  🗑️
                </button>
              </div>
            ))
          )}
        </div>

        <div className="log-detail">
          {selectedLog ? (
            <>
              <h2>請求詳情</h2>
              <div className="detail-section">
                <h3>基本資訊</h3>
                <table>
                  <tbody>
                    <tr>
                      <td>時間</td>
                      <td>{formatTimestamp(selectedLog.createdAt)}</td>
                    </tr>
                    <tr>
                      <td>方法</td>
                      <td><span className={`method-badge ${selectedLog.method?.toLowerCase()}`}>{selectedLog.method}</span></td>
                    </tr>
                    <tr>
                      <td>URL</td>
                      <td>{selectedLog.url}</td>
                    </tr>
                    <tr>
                      <td>IP</td>
                      <td>{selectedLog.ip}</td>
                    </tr>
                    <tr>
                      <td>User Agent</td>
                      <td style={{ fontSize: '12px', wordBreak: 'break-all' }}>{selectedLog.userAgent}</td>
                    </tr>
                    <tr>
                      <td>Content Type</td>
                      <td>{selectedLog.contentType}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {selectedLog.query && Object.keys(selectedLog.query).length > 0 && (
                <div className="detail-section">
                  <h3>Query 參數</h3>
                  <pre>{JSON.stringify(selectedLog.query, null, 2)}</pre>
                  <button onClick={() => copyToClipboard(JSON.stringify(selectedLog.query, null, 2))}>
                    📋 複製 JSON
                  </button>
                </div>
              )}

              {selectedLog.body && Object.keys(selectedLog.body).length > 0 && (
                <div className="detail-section">
                  <h3>Body 內容</h3>
                  <pre>{JSON.stringify(selectedLog.body, null, 2)}</pre>
                  <button onClick={() => copyToClipboard(JSON.stringify(selectedLog.body, null, 2))}>
                    📋 複製 JSON
                  </button>
                </div>
              )}

              {selectedLog.headers && (
                <div className="detail-section">
                  <h3>Headers</h3>
                  <pre>{JSON.stringify(selectedLog.headers, null, 2)}</pre>
                  <button onClick={() => copyToClipboard(JSON.stringify(selectedLog.headers, null, 2))}>
                    📋 複製 JSON
                  </button>
                </div>
              )}

              {selectedLog.error && (
                <div className="detail-section error">
                  <h3>錯誤訊息</h3>
                  <pre>{selectedLog.error}</pre>
                  {selectedLog.stack && <pre>{selectedLog.stack}</pre>}
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">請選擇一條記錄查看詳情</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default WebhookLogs;
